'use client';
import React from 'react';
import { Zap, ShieldAlert, Sparkles, Sliders, ArrowRight, CheckCircle2, ChevronRight } from 'lucide-react';
import Link from 'next/link';
import { motion } from 'framer-motion';

import JsonLd from '@/components/common/JsonLd';
import FAQSection from '@/components/common/FAQSection';
import ServiceSpecs from '@/components/sections/services/ServiceSpecs';
import CTASection from '@/components/sections/CTASection';

const faqs = [
    { 
        question: "What is the difference between digital printing and flexography?", 
        answer: "Digital printing uses high-tech digital presses to transfer ink directly onto the substrate without physical plates. Flexography utilizes custom flexible rubber plates. Digital is perfect for short-to-medium runs, multiple SKUs, and zero plate charges, while Flexography is best for very high-volume, static runs." 
    },
    { 
        question: "Can I print variable data like individual QR codes or consecutive numbers?", 
        answer: "Yes! One of the biggest advantages of digital packaging labeling is variable data printing (VDP). We can print unique serial numbers, custom QR codes, barcodes, or distinct product information on every single label in the batch." 
    },
    { 
        question: "Are digital packaging labels suitable for food and beverage applications?", 
        answer: "Absolutely. We use food-safe toners and high-durability substrates (like waterproof White BOPP or Clear PET) that withstand refrigeration, moisture, and oils, making them ideal for gourmet foods, craft beers, juices, and spirits." 
    },
    { 
        question: "Do you charge setup or plate fees?", 
        answer: "No. Digital printing requires no physical plates, meaning there are zero plate charges or setup fees. You only pay for the materials, printing, and custom die-cutting." 
    }
];

const digitalSpecs = {
    sampleAvailability: "Custom digital proofs and material samples available.",
    complianceStandards: "FDA-compliant adhesives, ISO 9001:2015 certified process",
    data: [
        { label: "Minimum Order Quantity (MOQ)", value: "Low starting points (500 to 2,500+ labels, ideal for start-ups & short runs)" },
        { label: "Standard Lead Time", value: "Super-fast 3 - 5 business days from artwork approval" },
        { label: "Printing Technology", value: "High-Resolution Indigo & UV Inkjet Digital Presses" },
        { label: "Resolution & Clarity", value: "Up to 1200 DPI photographic print quality" },
        { label: "Plate & Setup Costs", value: "Rs. 0 (No physical plates or setup fees required)" },
        { label: "Material Options", value: "Gloss/Matte Paper, Clear PET, Metallic Foils, White BOPP, Kraft Paper" },
        { label: "Variable Data Support", value: "Full variable text, serialization, unique QR/barcodes on every label" },
        { label: "Finishing Capabilities", value: "Spot UV, Soft-touch Matte, Gloss/Matte Lamination, Cold Foiling" },
        { label: "Color Gamut", value: "CMYK + Spot Colors (White, Silver, Orange, Violet, Green)" }
    ]
};

const DigitalPackagingLabelsPage = () => {
    return (
        <main className="w-full min-h-screen bg-white">
            <JsonLd 
                type="Service" 
                data={{
                    name: "Digital Packaging Labels",
                    description: "High-resolution digital printing for fast turnarounds, custom shapes, and variable design packaging. Ideal for short runs, seasonal products, and multi-SKU product lines.",
                    serviceType: "Industrial Label Printing",
                    category: "Packaging Solutions",
                    url: "https://royalprimelabels.com/services/digital-packaging-labels",
                    image: "https://royalprimelabels.com/images/service_barcode_labels.png",
                    mainEntityOfPage: "https://royalprimelabels.com/services/digital-packaging-labels"
                }} 
            />

            {/* Custom Premium Hero Section */}
            <section className="relative bg-[#050f19] text-white min-h-screen flex items-start overflow-hidden">
                {/* Glowing decorative background elements */}
                <div className="absolute top-1/4 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>

                <div className="rpl-hero-frame relative z-10 w-full">
                    {/* Breadcrumb */}
                    <nav className="flex items-center gap-1.5 text-white/60 text-sm font-medium mb-8">
                        <Link href="/" className="hover:text-white transition-colors">Home</Link>
                        <ChevronRight size={14} />
                        <Link href="/services" className="hover:text-white transition-colors">Our Services</Link>
                        <ChevronRight size={14} />
                        <span className="text-white">Digital Packaging Labels</span>
                    </nav>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                        <div className="flex flex-col items-start text-left gap-4">
                            <span className="inline-block px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-amber-400 text-xs font-bold tracking-[3px] uppercase">
                                On-Demand Printing
                            </span>
                            <h1 className="text-left text-white font-bold leading-tight" style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}>
                                Digital Packaging <br />
                                <span className="text-slate-400 font-serif italic">Labels &amp; Short-Runs</span>
                            </h1>
                            <p className="text-slate-300 text-base leading-relaxed max-w-xl">
                                Eliminate plate costs and lead times. Our high-resolution industrial digital label printing delivers photographic quality, custom die-cut shapes, and multi-SKU flexibility in record time.
                            </p>
                            <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto mt-4">
                                <Link 
                                    href="/quote"
                                    className="w-full sm:w-auto px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-full transition-all text-center shadow-lg hover:-translate-y-0.5"
                                >
                                    Get Short-Run Quote
                                </Link>
                                <Link 
                                    href="/contact"
                                    className="w-full sm:w-auto px-8 py-4 border border-white/20 hover:bg-white/5 text-white font-bold rounded-full transition-all text-center"
                                >
                                    Consult Experts
                                </Link>
                            </div>
                        </div>
                        <div className="relative">
                            <div className="bg-white/5 border border-white/10 p-4 rounded-3xl backdrop-blur-md">
                                <img 
                                    src="/images/service_barcode_labels.png" 
                                    alt="Digital packaging labels production roll" 
                                    className="w-full h-auto rounded-2xl object-cover shadow-2xl"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* "What Are" Section */}
            <section className="py-20 md:py-28 bg-white">
                <div className="w-full max-w-[1240px] mx-auto px-6 lg:px-0">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                        <div className="lg:col-span-5 flex flex-col items-start text-left">
                            <span className="w-12 h-1 bg-blue-600 rounded-full mb-6"></span>
                            <h2 className="text-3xl md:text-4xl font-bold text-[#0a1f33] leading-tight mb-6">
                                Photographic Precision. <br />
                                Zero Setup Friction.
                            </h2>
                            <p className="text-[#5A6782] text-lg leading-relaxed mb-6">
                                For brands with multiple product variations, seasonal campaigns, or growing batch requirements, traditional flexographic plates introduce heavy setup fees and operational friction.
                            </p>
                            <p className="text-[#5A6782] text-lg leading-relaxed">
                                Our state-of-the-art digital presses print directly from design files onto premium carrier materials, yielding ultra-sharp typography, perfect gradients, and flawless alignment.
                            </p>
                        </div>
                        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100 flex flex-col gap-4 text-left">
                                <div className="p-3 bg-blue-100 text-blue-600 rounded-xl w-fit">
                                    <Zap size={24} />
                                </div>
                                <h3 className="text-xl font-bold text-[#0a1f33]">Lightning Speed</h3>
                                <p className="text-slate-500 text-sm leading-relaxed">
                                    Receive your finished custom labels in as fast as 3-5 days. Perfect for critical launches and restocking.
                                </p>
                            </div>
                            <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100 flex flex-col gap-4 text-left">
                                <div className="p-3 bg-teal-100 text-teal-600 rounded-xl w-fit">
                                    <Sparkles size={24} />
                                </div>
                                <h3 className="text-xl font-bold text-[#0a1f33]">No Plate Setup</h3>
                                <p className="text-slate-500 text-sm leading-relaxed">
                                    Rs. 0 plate costs. Modify designs and add SKUs instantly without purchasing expensive physical cylinders or dies.
                                </p>
                            </div>
                            <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100 flex flex-col gap-4 text-left">
                                <div className="p-3 bg-amber-100 text-amber-600 rounded-xl w-fit">
                                    <Sliders size={24} />
                                </div>
                                <h3 className="text-xl font-bold text-[#0a1f33]">Variable Content</h3>
                                <p className="text-slate-500 text-sm leading-relaxed">
                                    Print serialized numbers, distinct barcodes, or unique QR codes on every packaging label dynamically.
                                </p>
                            </div>
                            <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100 flex flex-col gap-4 text-left">
                                <div className="p-3 bg-purple-100 text-purple-600 rounded-xl w-fit">
                                    <CheckCircle2 size={24} />
                                </div>
                                <h3 className="text-xl font-bold text-[#0a1f33]">Multi-SKU Harmony</h3>
                                <p className="text-slate-500 text-sm leading-relaxed">
                                    Combine flavors, scents, or product variants under a single print run to take advantage of bulk volume discounts.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Specs Section */}
            <ServiceSpecs specs={digitalSpecs} />

            {/* Industries Section */}
            <section className="py-20 md:py-28 bg-white">
                <div className="w-full max-w-[1240px] mx-auto px-6 lg:px-0">
                    <div className="text-center max-w-2xl mx-auto mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-[#0a1f33] mb-4">
                            Engineered for Premium Markets
                        </h2>
                        <p className="text-[#5A6782] text-lg leading-relaxed">
                            Discover how dynamic brands across high-value sectors utilize our digital label printing capabilities to stand out.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="flex flex-col rounded-3xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-md transition-all">
                            <div className="h-48 overflow-hidden">
                                <img src="/images/premium_liquor.png" alt="Liquor bottles labels" className="w-full h-full object-cover" />
                            </div>
                            <div className="p-6 flex flex-col gap-3 text-left">
                                <h3 className="text-xl font-bold text-[#0a1f33]">Craft Spirits & Wines</h3>
                                <p className="text-slate-500 text-sm leading-relaxed">
                                    Utilize high-texture metallic and clear-on-clear labels. Ideal for low-volume barrel finishes and limited editions.
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-col rounded-3xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-md transition-all">
                            <div className="h-48 overflow-hidden">
                                <img src="/images/luxury_cosmetic.png" alt="Cosmetics packaging labels" className="w-full h-full object-cover" />
                            </div>
                            <div className="p-6 flex flex-col gap-3 text-left">
                                <h3 className="text-xl font-bold text-[#0a1f33]">Boutique Cosmetics</h3>
                                <p className="text-slate-500 text-sm leading-relaxed">
                                    Matte, gloss, and soft-touch laminates protect labels against cosmetic oils while rendering beautiful gradient designs.
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-col rounded-3xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-md transition-all">
                            <div className="h-48 overflow-hidden">
                                <img src="/images/premium_food_beverage_v2.png" alt="Gourmet food packaging labels" className="w-full h-full object-cover" />
                            </div>
                            <div className="p-6 flex flex-col gap-3 text-left">
                                <h3 className="text-xl font-bold text-[#0a1f33]">Gourmet & Organic Foods</h3>
                                <p className="text-slate-500 text-sm leading-relaxed">
                                    FSC-certified papers and biodegradable clear substrates designed to withstand dampness, refrigeration, and handling.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQs */}
            <FAQSection 
                items={faqs} 
                title="Digital Packaging Label FAQs"
                description="Everything you need to know about our digital short-run printing capabilities and order placement."
            />

            {/* CTA */}
            <CTASection
                title="Ready to Print Something Brilliant?"
                description="Connect with our packaging specialists today to review your designs, choose the optimal substrate, and receive a rapid quote."
                buttonText="Start Your Short Run"
                href="/contact"
            />
        </main>
    );
};

export default DigitalPackagingLabelsPage;
